import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Home, CreditCard, LayoutGrid, User } from "lucide-react";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import Swal from "sweetalert2";
import Logo from "../assets/icons/logo_image.png";

const TopNav: React.FC = () => {
  const [userStatus, setUserStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const db = getFirestore();

  // Fetch user's status
  useEffect(() => {
    const fetchUserStatus = async () => {
      try {
        const auth = getAuth();
        const userId = auth.currentUser?.uid;

        if (!userId) {
          console.error("No user is logged in");
          setUserStatus(null);
          setLoading(false);
          return;
        }

        const userDocRef = doc(db, "users", userId);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUserStatus(userData?.status);
        } else {
          console.error("User document not found");
          setUserStatus(null);
        }
      } catch (error) {
        console.error("Error fetching user status:", error);
        setUserStatus(null);
      } finally {
        setLoading(false);
      }
    };

    fetchUserStatus();
  }, [db]);

  // Handle restricted navigation
  const handleNavigation = (route: string) => {
    if (loading) {
      Swal.fire({
        icon: "info",
        title: "Loading...",
        text: "Please wait while we check your account status.",
        showConfirmButton: false,
        timer: 1500,
      });
      return;
    }

    if (route === "/quizscreen") {
      if (userStatus === "verified") {
        navigate(route); // Allow navigation if verified
      } else if (userStatus === "unverified") {
        Swal.fire({
          icon: "warning",
          title: "Account Not Verified",
          text: "Please verify your account to access this feature.",
          confirmButtonText: "Okay",
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Unable to determine your account status. Please try again later.",
          confirmButtonText: "Okay",
        });
      }
    } else {
      navigate(route); // Allow navigation for all other routes
    }
  };

  return (
    <div className="hidden sm:flex justify-between items-center px-4 py-3 bg-gray-900 shadow-md">
      <img src={Logo} alt="RewardIQ Logo" className="w-16" />
      <span className="text-xl font-semibold text-white">Dashboard</span>
      <div className="flex gap-4">
        {[
          { label: "Dashboard", route: "/dashboard", icon: <Home size={18} /> },
          { label: "History", route: "/history", icon: <CreditCard size={18} /> },
          { label: "Games", route: "/quizscreen", icon: <LayoutGrid size={18} /> },
          { label: "Profile", route: "/UserProfile", icon: <User size={18} /> },
        ].map((item, index) => (
          <div
            key={index}
            onClick={() => handleNavigation(item.route)}
            className={`flex items-center gap-1 text-sm font-medium cursor-pointer ${
              index === 0 ? "text-white" : "text-gray-400 hover:text-white"
            }`}
          >
            {item.icon}
            <span>{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TopNav;
